import { useEffect, useState, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Replaces window.confirm with a branded Fitawrari FX AlertDialog.
 * Mount once at app root.
 */
export const BrandedConfirmProvider = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    const original = window.confirm;
    // Override to return a Promise-like via a synchronous shim.
    // Most callers use `if (!confirm(...)) return;` which expects a boolean.
    // We use a synchronous user-visible modal via async wrapper that pauses
    // execution by returning a thenable cast to boolean — but truly
    // synchronous confirm is impossible. Instead, monkey-patch as async.
    (window as any).confirm = (msg?: string): Promise<boolean> => {
      setMessage(msg || "Are you sure?");
      setOpen(true);
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
      });
    };
    return () => {
      window.confirm = original;
    };
  }, []);

  const handleClose = (result: boolean) => {
    setOpen(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && handleClose(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Action</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handleClose(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleClose(true)}>Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
